import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { INFLUENCER_CODE_TEXT } from '../constants';
import { useValidateBonusCode } from '../hook/use-validate-bonus-code';
import { BonusCodeDialog } from './BonusCodeDialog';

const IS_CHINA = SITE === 'china';

/**
 * 达人合作码入口（仅国内站，单行展示）。
 * - 未填写：单行文字按钮（半透明），点击打开输入弹窗；
 * - 已填写：Figma node-id=32643-6037 设计——圆角容器 + 文案 + 编辑 icon + 整体 opacity 64%，
 *   点击进入编辑弹窗。
 */
export const BonusCode = ({
  bonusCodeProps,
  setBonusCodeProps,
  className,
}: {
  bonusCodeProps?: string | undefined;
  setBonusCodeProps?: (bonusCode: string) => void;
  className?: string;
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [dialogSource, setDialogSource] = useState<'input' | 'edit'>('input');
  const [initialCode, setInitialCode] = useState<string>('');
  const [initialError, setInitialError] = useState<string>('');

  const { bonusCode, updateBonusCode } = useStore((state) => ({
    bonusCode: state.bonusCode,
    updateBonusCode: state.updateBonusCode,
  }));

  const { validateBonusCode } = useValidateBonusCode({
    onValidate: (error) => {
      if (!error && bonusCodeProps) {
        updateBonusCode(bonusCodeProps);
        reportEvent('apply_promo_code', {
          from_source: 'url',
          page_name: pathname,
        });
      } else if (error && bonusCodeProps) {
        setInitialCode(bonusCodeProps);
        setInitialError(
          error === 'UserHasSubscriptions'
            ? t('bonusCode.dialog.errorUserHasSubscriptions')
            : INFLUENCER_CODE_TEXT.paymentError,
        );
        setOpen(true);
      }
      setBonusCodeProps?.('');
    },
  });

  useEffect(() => {
    if (bonusCodeProps && !bonusCode) {
      validateBonusCode(bonusCodeProps);
    }
  }, [bonusCodeProps, bonusCode]);

  useEffect(() => {
    if (IS_CHINA) {
      reportEvent('promo_code_exposure', {
        page_name: pathname,
      });
    }
  }, []);

  const handleOpenDialog = (source: 'input' | 'edit') => {
    reportEvent('apply_promo_code_click', {
      page_name: pathname,
    });
    if (IS_CHINA) {
      reportEvent('cpp_code_click', {
        vip_price_cpp_code: source,
        cpp_code_input_popup: 'history',
        cpp_code_id: 'submit',
      });
    }
    setDialogSource(source);
    setInitialCode('');
    setInitialError('');
    setOpen(true);
  };

  // 福利码已下线，仅国内站展示达人合作码入口
  if (!IS_CHINA) {
    return null;
  }

  const getUsedLabel = (code: string) => `${INFLUENCER_CODE_TEXT.entry.coopCode}：${code}`;

  return (
    <>
      {bonusCode ? (
        <button
          type="button"
          onClick={() => handleOpenDialog('edit')}
          className={cn(
            'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-12 px-4 py-3 text-sm text-white opacity-[0.64] transition-opacity hover:opacity-[0.88]',
            className,
          )}
        >
          <span className="leading-[1.4]">{getUsedLabel(bonusCode)}</span>
          <BaseLineEdit className="size-4 shrink-0" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => handleOpenDialog('input')}
          className={cn(
            'cursor-pointer whitespace-nowrap text-sm text-white opacity-[0.64] transition-opacity hover:opacity-[0.88]',
            className,
          )}
        >
          {INFLUENCER_CODE_TEXT.entry.coopCode}
        </button>
      )}

      <BonusCodeDialog
        bonusCode={bonusCode}
        open={open}
        onOpenChange={setOpen}
        initialCode={initialCode}
        initialError={initialError}
        source={dialogSource}
      />
    </>
  );
};
